import { useState } from 'react'
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Modal,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core'
import {
  IconAlertTriangle,
  IconRefresh,
  IconTable,
} from '@tabler/icons-react'

import { VALVE_FEATURE_MIN_FIRMWARE } from '@data/device/model/device'
import {
  VALVE_STATUS_KEYS,
  valveCommSlotKey,
  ValveRecoveryLastAPI,
  ValveRecoveryOutcome,
  ValveStatusAPI,
  ValveStatusCommand,
  valveStatusExtendedSlotKey,
  ValveStatusPosition,
} from '@data/device/model/valveStatus'
import {
  useValveStatus,
  type ValveTarget,
} from '@data/device/device'
import { describeApiError } from '@common/utils/apiErrors'
import {
  formatFirmwareVersion,
  isFirmwareAtLeast,
} from '@common/utils/firmwareVersion'

import { VALVE_STATE_META } from './valveStateMeta'

const POSITION_LABEL: Record<ValveStatusPosition, string> = {
  0: 'Closed',
  1: 'Open',
  2: 'Mid Position',
  9: 'Unknown',
}

const COMMAND_LABEL: Record<ValveStatusCommand, string> = {
  0: 'Close',
  1: 'Open',
  2: 'Test',
  3: 'None',
}

const RECOVERY_OUTCOME_LABEL: Record<ValveRecoveryOutcome, string> = {
  CONFIRMED: 'Confirmed',
  FOUND: 'Found',
  RESTORED: 'Restored',
  FAILSAFE_CLOSED: 'Failsafe Closed',
  ADOPTED: 'Adopted',
  UNKNOWN: 'Unknown',
  NOT_FITTED: 'Not Fitted',
}

const RECOVERY_SOURCE_LABEL: Record<string, string> = {
  boot: 'Boot',
  dc_restore: 'DC Restore',
  probe: 'Probe',
}

// Device RTC epoch — can be 0 or wrong if the device clock was never set. An implausible
// value (before this feature could exist) is shown as unknown rather than as 1970.
const EARLIEST_PLAUSIBLE_EPOCH = 1_700_000_000 // ~2023-11-14

const formatEpoch = (epoch: number): string =>
  epoch >= EARLIEST_PLAUSIBLE_EPOCH
    ? new Date(epoch * 1000).toLocaleString()
    : 'Unknown'

const CommissioningDataTable = ({ data }: { data: ValveStatusAPI }) => (
  <Stack gap="xs">
    <Text fw={600} size="sm">
      Commissioning Data
    </Text>

    <Table withTableBorder withColumnBorders>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Valve</Table.Th>
          <Table.Th>Final Status</Table.Th>
          <Table.Th>Open Result</Table.Th>
          <Table.Th>Close Result</Table.Th>
          <Table.Th>Open Travel (ms)</Table.Th>
          <Table.Th>Close Travel (ms)</Table.Th>
        </Table.Tr>
      </Table.Thead>

      <Table.Tbody>
        {VALVE_STATUS_KEYS.map((key) => {
          const comm = data[valveCommSlotKey(key)]

          return (
            <Table.Tr key={key}>
              <Table.Td fw={600}>{key}</Table.Td>
              {comm ? (
                <>
                  <Table.Td>{VALVE_STATE_META[comm.s].label}</Table.Td>
                  <Table.Td>{VALVE_STATE_META[comm.o].label}</Table.Td>
                  <Table.Td>{VALVE_STATE_META[comm.c].label}</Table.Td>
                  <Table.Td>{comm.ot || '—'}</Table.Td>
                  <Table.Td>{comm.ct || '—'}</Table.Td>
                </>
              ) : (
                <Table.Td colSpan={5} c="dimmed">Not commissioned</Table.Td>
              )}
            </Table.Tr>
          )
        })}
      </Table.Tbody>
    </Table>
  </Stack>
)

const ExtendedDataTable = ({ data }: { data: ValveStatusAPI }) => (
  <Stack gap="xs">
    <Text fw={600} size="sm">
      Extended Data
    </Text>

    <Table withTableBorder withColumnBorders>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Valve</Table.Th>
          <Table.Th>Position</Table.Th>
          <Table.Th>State</Table.Th>
          <Table.Th>Command</Table.Th>
          <Table.Th>Travel (ms)</Table.Th>
        </Table.Tr>
      </Table.Thead>

      <Table.Tbody>
        {VALVE_STATUS_KEYS.map((key) => {
          const extended = data[valveStatusExtendedSlotKey(key)]

          return (
            <Table.Tr key={key}>
              <Table.Td fw={600}>{key}</Table.Td>
              {extended ? (
                <>
                  <Table.Td>{POSITION_LABEL[extended.p]}</Table.Td>
                  <Table.Td>{VALVE_STATE_META[extended.s].label}</Table.Td>
                  <Table.Td>{COMMAND_LABEL[extended.c]}</Table.Td>
                  <Table.Td>{extended.t || '—'}</Table.Td>
                </>
              ) : (
                <Table.Td colSpan={4} c="dimmed">No data</Table.Td>
              )}
            </Table.Tr>
          )
        })}
      </Table.Tbody>
    </Table>
  </Stack>
)

const RecoveryDataTable = ({ recoveryLast }: { recoveryLast: ValveRecoveryLastAPI | null }) => (
  <Stack gap="xs">
    <Text fw={600} size="sm">
      Recovery Data
    </Text>

    {recoveryLast ? (
      <>
        <Group gap="md">
          <Text size="sm">
            <Text span fw={600}>Trigger:</Text>{' '}
            {RECOVERY_SOURCE_LABEL[recoveryLast.src] ?? recoveryLast.src}
          </Text>
          <Text size="sm">
            <Text span fw={600}>Swept at:</Text>{' '}
            {formatEpoch(recoveryLast.epoch)}
          </Text>
        </Group>

        <Table withTableBorder withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Valve</Table.Th>
              <Table.Th>Position</Table.Th>
              <Table.Th>State</Table.Th>
              <Table.Th>Command</Table.Th>
              <Table.Th>Outcome</Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {VALVE_STATUS_KEYS.map((key) => {
              const result = recoveryLast.results[key]

              return (
                <Table.Tr key={key}>
                  <Table.Td fw={600}>{key}</Table.Td>
                  {result ? (
                    <>
                      <Table.Td>{POSITION_LABEL[result.p]}</Table.Td>
                      <Table.Td>{VALVE_STATE_META[result.s].label}</Table.Td>
                      <Table.Td>{COMMAND_LABEL[result.c]}</Table.Td>
                      <Table.Td>
                        <Badge size="sm" variant="light">
                          {RECOVERY_OUTCOME_LABEL[result.r]}
                        </Badge>
                      </Table.Td>
                    </>
                  ) : (
                    <Table.Td colSpan={4} c="dimmed">Not swept</Table.Td>
                  )}
                </Table.Tr>
              )
            })}
          </Table.Tbody>
        </Table>
      </>
    ) : (
      <Text size="sm" c="dimmed">
        No recovery sweep recorded yet.
      </Text>
    )}
  </Stack>
)

export interface ValveAdvancedDataProps {
  target: ValveTarget
  firmware: string
}

// Trigger + modal for the read-only diagnostic snapshot behind GET .../valve_status —
// only available on firmware new enough to have the endpoint at all.
// Fetched only while the modal is open — see useValveStatus.
export const ValveAdvancedData = ({ target, firmware }: ValveAdvancedDataProps) => {
  const [opened, setOpened] = useState(false)
  const {
    data,
    isFetching,
    isError,
    error,
    refetch,
  } = useValveStatus(target, opened)

  const isFirmwareSupported = isFirmwareAtLeast(firmware, VALVE_FEATURE_MIN_FIRMWARE)
  const minFirmwareVersion = formatFirmwareVersion(VALVE_FEATURE_MIN_FIRMWARE)

  return (
    <>
      <Tooltip
        label={`Requires firmware ${minFirmwareVersion} or later.`}
        disabled={isFirmwareSupported}
      >
        {/* Wrapped in a span — Tooltip can't reliably attach hover listeners directly to a
            disabled button, since disabled elements suppress pointer events in some browsers. */}
        <span>
          <Button
            variant="default"
            size="xs"
            leftSection={<IconTable size={14} />}
            onClick={() => setOpened(true)}
            disabled={!isFirmwareSupported}
          >
            View advanced valve data
          </Button>
        </span>
      </Tooltip>

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={
          <Group gap="xs" wrap="nowrap">
            <Text fw={600}>Advanced Valve Data</Text>

            <Tooltip label="Refresh">
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                onClick={() => refetch()}
                loading={isFetching}
                aria-label="Refresh valve data"
              >
                <IconRefresh size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        }
        size="xl"
      >
        {/* Only the initial load blocks the view — a refresh keeps the current data on
            screen while it runs, signalled by the header icon's own loading state. */}
        {isFetching && !data && (
          <Group justify="center" p="lg">
            <Loader size="sm" />
          </Group>
        )}

        {isError && (
          <Alert
            color="red"
            variant="light"
            icon={<IconAlertTriangle size={16} />}
          >
            {describeApiError(error, 'Failed to load valve data.')}
          </Alert>
        )}

        {data && (
          <Stack gap="lg">
            <CommissioningDataTable data={data} />
            <ExtendedDataTable data={data} />
            <RecoveryDataTable recoveryLast={data.valveRecoveryLast} />
          </Stack>
        )}
      </Modal>
    </>
  )
}
